import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, RevealLinesOnView } from "@/components/motion/Reveals";
import { useLang } from "@/i18n/LangContext";
import { useContent } from "@/cms/ContentContext";
import { useLeafTx } from "@/cms/useTexts";
import { useSpacing } from "@/cms/useSpacing";

const Testemunhos = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const tx = useLeafTx();
  const { sectionPy, titleGap, overlineGap, divider } = useSpacing();
  const [i, setI] = useState(0);
  const cmsItems = content?.testemunhos || [];
  const items = cmsItems.length
    ? cmsItems.map((it) => ({
        quote: lang === "pt" ? it.quote_pt : it.quote_en,
        author: it.author,
        role: lang === "pt" ? it.role_pt : it.role_en,
      }))
    : t.testemunhos.items;

  const next = () => setI((v) => (v + 1) % items.length);
  const prev = () => setI((v) => (v - 1 + items.length) % items.length);

  return (
    <section id="testemunhos" data-testid="testemunhos-section" className={`relative ${sectionPy("testemunhos")} ${divider()}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className={`overline overline-2x text-[#6B6554] mb-10 md:mb-14 ${overlineGap("testemunhos")}`}>{tx("testemunhos.overline", t.testemunhos.overline)}</div>
        </FadeUp>
        <div className="relative grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-9 relative min-h-[280px]">
            <div
              aria-hidden
              className="absolute -top-16 -left-4 md:-left-8 font-serif text-[#B0926A]/30 text-[220px] leading-none pointer-events-none select-none"
            >
              “
            </div>
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: [0.7, 0, 0.15, 1] }}
                className="relative"
                data-testid={`testimonial-${i}`}
              >
                <p className="font-montserrat-light text-2xl sm:text-3xl md:text-4xl leading-[1.4] text-[#3A3524] max-w-3xl">
                  {items[i].quote}
                </p>
                <footer className="mt-8 flex items-center gap-4">
                  <span className="block w-10 h-px bg-[#3A3524]/50" />
                  <div>
                    <div className="text-[#3A3524] font-medium tracking-wide">{items[i].author}</div>
                    <div className="overline text-[#6B6554]">{items[i].role}</div>
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          <div className="col-span-12 lg:col-span-3 flex flex-col gap-4 items-start lg:items-end">
            <div className="font-serif text-[#3A3524] text-2xl">
              <span className="tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-[#3A3524]/40"> / {String(items.length).padStart(2, "0")}</span>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={prev}
                aria-label="Previous"
                className="w-12 h-12 border border-[#3A3524]/40 hover:border-[#3A3524] hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-all duration-500 flex items-center justify-center"
              >
                ←
              </button>
              <button
                data-testid="testimonial-next"
                onClick={next}
                aria-label="Next"
                className="w-12 h-12 border border-[#3A3524]/40 hover:border-[#3A3524] hover:bg-[#3A3524] hover:text-[#F5F2EC] transition-all duration-500 flex items-center justify-center"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testemunhos;
